import { useCallback } from 'react';
import type { LngLatBoundsLike } from 'maplibre-gl';
import type { MapRef } from '@vis.gl/react-maplibre';

export interface GeoPointLike {
  lat: number;
  lon: number;
}

// Consolidates the ~16 duplicated `camera.flyTo(Cartesian3.fromDegrees(...))` /
// `camera.setView(Rectangle.fromDegrees(...))` call sites into MapLibre's native
// flyTo/fitBounds. Cesium's `altitude` (metres) and MapLibre's `zoom` (level) are
// not equivalent units — each call site's default zoom needs picking to match
// the old altitude's visual closeness, there is no formula to convert directly.
export const useMapFlyTo = (mapRef: React.RefObject<MapRef | null>) => {
  const flyTo = useCallback(
    (lat: number, lon: number, zoom = 17, durationMs = 1200) => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      map.flyTo({ center: [lon, lat], zoom, duration: durationMs });
    },
    [mapRef]
  );

  const fitBounds = useCallback(
    (points: GeoPointLike[], padding = 60, durationMs = 1200) => {
      const map = mapRef.current?.getMap();
      if (!map || points.length === 0) return;

      let minLon = points[0].lon;
      let maxLon = points[0].lon;
      let minLat = points[0].lat;
      let maxLat = points[0].lat;
      for (const { lat, lon } of points) {
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      }

      const bounds: LngLatBoundsLike = [
        [minLon, minLat],
        [maxLon, maxLat],
      ];
      map.fitBounds(bounds, { padding, duration: durationMs });
    },
    [mapRef]
  );

  return { flyTo, fitBounds };
};

export default useMapFlyTo;
